import {
  bindService,
  createServiceToken,
  type AppFeatureDefinition,
} from "@nasam/kernel";
import {
  alertHistoryRepositoryToken,
  assetsRepositoryToken,
  assetTypesRepositoryToken,
  branchesRepositoryToken,
  companiesRepositoryToken,
  devicesRepositoryToken,
  sensorAssignmentsRepositoryToken,
  type AssetsRepository,
  type AssetTypesRepository,
  type BranchesRepository,
  type CompaniesRepository,
  type DevicesRepository,
  type SensorAssignmentsRepository,
} from "../../contracts";
import {
  createAlertHistoryHttpRepository,
  createAlertsHttpRepository,
} from "./data";
import type { AlertsRepository } from "./domain";
import { createAlertsRoutes } from "./presentation/routes";
import { ALERT_HISTORY_VIEW, ALERTS_VIEW } from "./alertsPermissions";

export * from "./alertsPermissions";

export const alertsRepositoryToken =
  createServiceToken<AlertsRepository>("alerts.repository");

// Defined with the shared contracts, so Tracking can read Alert History too.
export { alertHistoryRepositoryToken };

export const alertsFeature: AppFeatureDefinition = {
  id: "alerts",
  version: "0.1.0",
  titleKey: "alerts:moduleTitle",
  icon: "Bell",
  basePath: "/alerts",
  requiredPermissions: [ALERTS_VIEW, ALERT_HISTORY_VIEW],
  // Every CORE feature shares one "core" rail node (see mergeFeatureNavNodes);
  // this feature contributes the "Notifications management" drawer group
  // under it.
  navigation: [
    {
      id: "core",
      titleKey: "common:businessModules.core",
      icon: "Layers",
      placement: "rail",
      order: 10,
      children: [
        {
          id: "alerts-management",
          titleKey: "alerts:permissions.alertManagement",
          icon: "Bell",
          placement: "drawer",
          order: 80,
          requiredPermissions: [ALERTS_VIEW, ALERT_HISTORY_VIEW],
          children: [
            {
              id: "alerts",
              titleKey: "alerts:moduleTitle",
              icon: "Bell",
              placement: "drawer",
              order: 10,
              path: "/alerts",
              requiredPermissions: [ALERTS_VIEW],
            },
            {
              id: "alerts-log",
              titleKey: "alerts:nav.log",
              icon: "FileText",
              placement: "drawer",
              order: 20,
              path: "/alerts/log",
              requiredPermissions: [ALERT_HISTORY_VIEW],
            },
          ],
        },
      ],
    },
  ],
  routes: (context) =>
    createAlertsRoutes({
      context,
      alerts: context.services.resolve(alertsRepositoryToken),
      history: context.services.resolve(alertHistoryRepositoryToken),
      companies: context.services.tryResolve<CompaniesRepository>(
        companiesRepositoryToken,
      ),
      branches: context.services.tryResolve<BranchesRepository>(
        branchesRepositoryToken,
      ),
      assets: context.services.tryResolve<AssetsRepository>(
        assetsRepositoryToken,
      ),
      assetTypes: context.services.tryResolve<AssetTypesRepository>(
        assetTypesRepositoryToken,
      ),
      devices: context.services.tryResolve<DevicesRepository>(
        devicesRepositoryToken,
      ),
      sensors: context.services.tryResolve<SensorAssignmentsRepository>(
        sensorAssignmentsRepositoryToken,
      ),
    }),
  provideServices: ({ httpClient }) => [
    bindService(alertsRepositoryToken, createAlertsHttpRepository(httpClient)),
    bindService(
      alertHistoryRepositoryToken,
      createAlertHistoryHttpRepository(httpClient),
    ),
  ],
  permissionCatalog: [
    {
      segment: "core",
      labelKey: "alerts:permissions.core",
      children: [
        {
          segment: "alert_management",
          labelKey: "alerts:permissions.alertManagement",
          children: [
            {
              segment: "alert_management_list",
              labelKey: "alerts:permissions.alerts",
              actions: [
                { id: "view", labelKey: "alerts:actions.view" },
                { id: "create", labelKey: "alerts:actions.create" },
                { id: "update", labelKey: "alerts:actions.update" },
                { id: "delete", labelKey: "alerts:actions.delete" },
              ],
            },
            {
              // Alert History: the backend's permission is view only.
              segment: "alert_history_list",
              labelKey: "alerts:permissions.log",
              actions: [{ id: "view", labelKey: "alerts:actions.view" }],
            },
          ],
        },
      ],
    },
  ],
};
